// Render a verb or adjective dictionary form with the inflectional
// ending visually factored out from the stem. Used wherever a card
// surface displays a lemma — the n5 lifted card, the reader popover
// token card, and the drill prompt's gloss chips.
//
// For non-verb / non-adjective POS, falls through to plain text with
// no extra wrapper or layout shift.

import {
  isAdjective,
  isVerb,
  splitAdjective,
  splitVerb,
} from "@bindery/ja-core";

type Size = "sm" | "md" | "lg";

interface Props {
  display: string;
  pos: string;
  /** Hiragana / katakana reading. Used only at size='lg' to render the
   * linguistic romaji split underneath the factored display. */
  reading?: string;
  size?: Size;
}

export function MorphologyDisplay({
  display,
  pos,
  reading,
  size = "md",
}: Props) {
  if (isVerb(pos)) {
    const split = splitVerb(display, pos, reading);
    if (split.kind === "irregular" && split.stem === "") {
      return <span style={{ color: "var(--ink)" }}>{display}</span>;
    }
    const showRomaji = size === "lg" && Boolean(split.romaji);
    if (!showRomaji) {
      return (
        <span>
          <span style={{ color: "var(--ink)" }}>{split.stem}</span>
          <span style={{ color: "var(--ink-soft)" }}>{split.ending}</span>
        </span>
      );
    }
    return (
      <span className="inline-flex flex-col items-start">
        <span>
          <span style={{ color: "var(--ink)" }}>{split.stem}</span>
          <span style={{ color: "var(--ink-soft)" }}>{split.ending}</span>
        </span>
        <span className="n5-marginalia mt-0.5">
          {split.romaji!.stem}
          <span style={{ opacity: 0.6 }}>-</span>
          {split.romaji!.ending}
        </span>
      </span>
    );
  }

  if (isAdjective(pos)) {
    const split = splitAdjective(display, pos);
    if (split.kind === "い") {
      return (
        <span>
          <span style={{ color: "var(--ink)" }}>{split.stem}</span>
          <span style={{ color: "var(--ink-soft)" }}>{split.ending}</span>
        </span>
      );
    }
    // な-adjective: stem rendered normally, な glyph in marginalia
    // position to telegraph the attributive-only behavior.
    return (
      <span className="inline-flex items-baseline gap-1">
        <span style={{ color: "var(--ink)" }}>{split.stem}</span>
        <span
          aria-hidden
          style={{
            color: "var(--ink-soft)",
            fontStyle: "italic",
            fontSize: "0.7em",
            opacity: 0.85,
          }}
        >
          な
        </span>
      </span>
    );
  }

  return <>{display}</>;
}
